import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import { formatNegativeWithBrackets } from '../../../../common/utils/common.util';
import { ScheduleGraphRowDTO, SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import {
  CONSTRUCTION_PROGRESS_SCHEDULE_HAS_DATES_TEXT,
  CONSTRUCTION_PROGRESS_SCHEDULE_NO_DATES_TEXT,
  SCHEDULE_SECTION_CONFIG,
} from './sections.config';
import { CommentsSection } from './CommentsSection';
import { FieldRow } from './FieldRow';
import {
  SCHEDULE_CALC_MODES,
  SCHEDULE_NO_CALCULATION_TEXT,
  ScheduleCalcMode,
  calculateWeeksAheadBehind,
  calculateWeeksAheadBehindAllBudgets,
  getCommentsField,
  getScheduleBudgetLabel,
  getScheduleCommentText,
  parseScheduleCalcMode,
  resolveScheduleBudgetDraw,
} from './section-utils';
import { DeclarativeLayoutRenderer } from './DeclarativeLayoutRenderer';
import { useMagicReport } from '../context/MagicReportContext';
import { DateFieldRenderer } from './field-renderers/DateFieldRenderer';
import { SelectFieldRenderer } from './field-renderers/SelectFieldRenderer';
import { WeeksBehindFieldRenderer, getWeeksBehindDirection } from './field-renderers/WeeksBehindFieldRenderer';
import { BookmarkToggle } from './BookmarkToggle';
import { Button } from 'components/index';
import { prepareGraphData, SCHEDULE_GRAPH_LAYOUT } from '../../shared/steps/notes/utils/coverPageGraphs.util';
import CoverPageGraphs from '../../shared/steps/notes/cover-page/CoverPageGraphs';
import ScheduleGraphTable, { sortRows } from '../../shared/steps/budget/ScheduleGraphTable';

interface ScheduleSectionProps {
  magicReportId: string;
}

// The master toggle covers only the schedule figures that belong in the Action Items output: the
// key dates plus Liquidated Damages per Contract. The other icon-less fields (Certificate of
// Occupancy, Budget used for calculation, Weeks Behind, Calc Weeks Ahead(Behind), Will the Borrower
// assess Liquidated Damages, Comment on Labor/Materials, Labor/Materials Adequate) are inputs to the
// Schedule Section Text narrative — they must never be emitted as their own rows, so they are
// deliberately left out. Construction Progress Schedule text, Schedule Section Text, and Comments
// are excluded too — they keep their own bookmark icons and control only their own paragraph.
//
// Mirrored by Schedule.MasterBookmarkFieldNames on the backend, which applies these same
// bookmarks by default on reset and on workbook ingestion. Keep the two lists in sync or the
// toggle renders half-on (isMasterBookmarked below requires every field to be bookmarked).
const SCHEDULE_MASTER_BOOKMARK_FIELDS = [
  'CommencementDate',
  'CDCCEstimatedCommencementDate',
  'OriginalCompletionDate',
  'RevisedCompletionDate',
  'CDCCEstimatedCompletionDate',
  'BankLoanExpirationDate',
  'LiquidatedDamagesPerContract',
];

/** Display labels for the Schedule calculation mode select, and the reverse lookup for onChange. */
const CALC_MODE_LABELS: Record<ScheduleCalcMode, string> = {
  [SCHEDULE_CALC_MODES.single]: 'Single G702',
  [SCHEDULE_CALC_MODES.all]: 'All G702s',
  [SCHEDULE_CALC_MODES.none]: 'No Calculation',
};
const CALC_MODE_BY_LABEL: Record<string, ScheduleCalcMode> = Object.fromEntries(
  Object.entries(CALC_MODE_LABELS).map(([mode, label]) => [label, mode as ScheduleCalcMode]),
);

/** Shown before committing to All — the work in several G702s may not be concurrent. */
const ALL_G702_WARNING_TEXT =
  'Is the work in all G702s occurring at the same time, sequentially, or staggered? (sequentially meaning that the work on one ' +
  'G702 is finished before the next one is started.) If sequentially, it would be best to select the one G702 with the most ' +
  'recent activity. If staggered, then the calculation is unreliable, so it would be best to either select the one G702 with ' +
  'the most recent activity or select No Calculation.';

/**
 * Build project-like summary from schedule section date fields for weeks calculation.
 * Uses section field values so the calculation updates when user edits dates in the schedule section.
 */
function getProjectLikeFromSectionData(data: SectionFieldCollection) {
  const getVal = (name: string) => data?.fields.find(f => f.fieldName === name)?.value ?? '';
  return {
    commencementDate: getVal('CommencementDate'),
    revisedCompletionDate: getVal('RevisedCompletionDate'),
    originalCompletionDateEstimated: getVal('OriginalCompletionDate'),
  };
}

/**
 * Schedule Section Content
 * Now using declarative config-driven rendering!
 * Cal weeks ahead(behind) is computed from this section's date fields so it updates when user edits them.
 */
const ScheduleSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const { reportState, setReportState, setIsDirty } = useMagicReport();
  const theme = useTheme();
  const lastScheduleAutoTextRef = useRef<string | null>(null);
  const lastProgressScheduleAutoTextRef = useRef<string | null>(null);
  // Tracks whether WeeksBehindToReport has already been seeded with its default value (0) on mount.
  const weeksBehindSeededRef = useRef(false);

  // Funding schedule graph — moved here from the Amounts for Cover Page section. The axis dates are
  // derived from this section's own date fields (see graphData below) so the preview updates in
  // realtime as the user edits Commencement/Completion dates on the left.
  const [isGraphDialogOpen, setIsGraphDialogOpen] = useState(false);
  const [hoveredFundingId, setHoveredFundingId] = useState<string | null>(null);
  const scheduleGraph = reportState.sections?.scheduleGraph;
  const scheduleGraphAxis = reportState.sections?.scheduleGraphAxis;

  const handleScheduleGraphChange = (rows: ScheduleGraphRowDTO[]) => {
    setIsDirty(true);
    setReportState((prev: any) => ({ ...prev, sections: { ...prev.sections, scheduleGraph: rows } }));
  };

  const handleHideCommencementChange = (hideCommencement: boolean) => {
    setIsDirty(true);
    setReportState((prev: any) => ({
      ...prev,
      sections: { ...prev.sections, scheduleGraphAxis: { ...prev.sections?.scheduleGraphAxis, hideCommencement } },
    }));
  };

  // Auto-add a row for the current draw's payapp once its numbers are available, instead of
  // requiring someone to type them in by hand. % Complete comes from the payapp's own
  // system-computed totalContractBudgetPerComplete — not from amountFunded — since that figure
  // isn't something a user enters on the payapp; it's calculated for them. Guarded by
  // applicationDate (falling back to applicationNumber) so it won't add a duplicate if a row for
  // that period already exists (e.g. one entered by hand) — it only ever seeds once per draw and
  // is a normal, editable/deletable row after that (same pattern as the old isCurrentPeriod row).
  const didSeedCurrentDrawRowRef = useRef(false);
  useEffect(() => {
    if (didSeedCurrentDrawRowRef.current) return;
    const applicationNumber = reportState.drawSummary?.applicationNumber;
    const applicationDate = reportState.drawSummary?.periodEnding;
    const percentComplete = reportState.budgetSummary?.totalContractBudgetPerComplete;
    if (!applicationNumber || !applicationDate || percentComplete == null) return;
    // periodEnding can come off the API as a full ISO datetime (e.g. "2026-09-08T00:00:00.000Z")
    // while a row's applicationDate, once user-edited, is normalized to bare "yyyy-MM-dd" — so
    // compare just the date portion rather than the raw strings.
    const applicationDateOnly = applicationDate.split('T')[0];
    if (
      (scheduleGraph ?? []).some(r => r.applicationDate?.split('T')[0] === applicationDateOnly || r.applicationNumber === applicationNumber)
    ) {
      didSeedCurrentDrawRowRef.current = true;
      return;
    }
    didSeedCurrentDrawRowRef.current = true;
    const newRow: ScheduleGraphRowDTO = {
      id: crypto.randomUUID(),
      applicationNumber,
      applicationDate,
      amountFunded: undefined,
      percentComplete,
      comment: undefined,
      isLastMonth: false,
    };
    setIsDirty(true);
    setReportState((prev: any) => ({
      ...prev,
      // Sorted — other readers rely on scheduleGraph staying in ascending applicationDate order.
      sections: { ...prev.sections, scheduleGraph: sortRows([...(prev.sections?.scheduleGraph ?? []), newRow]) },
    }));
  }, [
    reportState.drawSummary?.applicationNumber,
    reportState.drawSummary?.periodEnding,
    reportState.budgetSummary?.totalContractBudgetPerComplete,
  ]);

  const graphData = useMemo(() => {
    const graphType = reportState.coverPage?.graphType || 'standard';
    if (graphType === 'none' || !reportState.budgetSummary) return undefined;
    return prepareGraphData(
      {
        budgetSummary: reportState.budgetSummary,
        drawSummary: reportState.drawSummary,
        coverPage: reportState.coverPage,
        scheduleSection: data,
      },
      undefined,
      scheduleGraph,
      scheduleGraphAxis,
    );
  }, [reportState.coverPage, reportState.budgetSummary, reportState.drawSummary, data, scheduleGraph, scheduleGraphAxis]);

  const layoutContainerRef = useRef<HTMLDivElement>(null);

  const commentsField = getCommentsField(data, SCHEDULE_SECTION_CONFIG.config);
  const budgetFieldName = 'WhichBudgetShouldBeUsedToCalculateTheSchedule';
  const budgetField = getField(budgetFieldName);
  const modeFieldName = 'ScheduleCalculationMode';
  const modeField = getField(modeFieldName);
  const calcWeeksFieldName = 'CalcWeeksAheadBehind';
  const calcWeeksField = getField(calcWeeksFieldName);
  const weeksBehindFieldName = 'WeeksBehindToReport';
  const weeksBehindField = getField(weeksBehindFieldName);

  // Every G702/3 this report covers. The calculation runs against one of them (Single), all of
  // them (All), or none (No Calculation) — see calcMode below.
  const coveredDraws = reportState.draws ?? [];
  const calcMode = parseScheduleCalcMode(modeField?.value);
  const selectedDraw = resolveScheduleBudgetDraw(coveredDraws, budgetField?.value);
  const budgetOptions = useMemo(
    () => coveredDraws.map((draw, index) => ({ id: draw.drawId, label: getScheduleBudgetLabel(draw, index) })),
    [coveredDraws],
  );
  // Warning gate for All mode: picking it opens the dialog, and only "Proceed with All" commits.
  const [isAllWarningOpen, setIsAllWarningOpen] = useState(false);

  // Compute Cal weeks ahead(behind) from schedule section date fields + the covered G702(s).
  // Recalculates whenever section date fields, the mode, or the selected budget change.
  const calcWeeksAheadBehind = useMemo(() => {
    if (calcMode === SCHEDULE_CALC_MODES.none) return SCHEDULE_NO_CALCULATION_TEXT;
    const projectLike = getProjectLikeFromSectionData(data);
    if (calcMode === SCHEDULE_CALC_MODES.all) {
      return calculateWeeksAheadBehindAllBudgets(projectLike, coveredDraws);
    }
    // Single: fall back to the report's primary draw mirror when the report predates `draws`.
    return calculateWeeksAheadBehind(
      projectLike,
      selectedDraw?.budgetSummary ?? reportState.budgetSummary,
      selectedDraw?.drawSummary ?? reportState.drawSummary,
    );
  }, [calcMode, data, coveredDraws, selectedDraw, reportState.budgetSummary, reportState.drawSummary]);

  const handleModeChange = (nextMode: ScheduleCalcMode) => {
    // All is destructive-ish to trust in the number, so it goes through the warning dialog first.
    if (nextMode === SCHEDULE_CALC_MODES.all) {
      setIsAllWarningOpen(true);
      return;
    }
    handleFieldChange(modeFieldName, { value: nextMode });
  };

  const commencementDateValue = getField('CommencementDate')?.value?.trim() || '';
  const cdccEstCommencementDateValue = getField('CDCCEstimatedCommencementDate')?.value?.trim() || '';
  const revisedCompletionDateValue = getField('RevisedCompletionDate')?.value?.trim() || '';

  // Either Commencement Date or CDCC Estimated Commencement Date must be present. If both are
  // present, they must match. Surfaced as a non-blocking warning — the inspector may proceed regardless.
  const commencementDateError = useMemo(() => {
    if (!commencementDateValue && !cdccEstCommencementDateValue) {
      return 'Commencement Date or CDCC Estimated Commencement Date is required.';
    }
    if (commencementDateValue && cdccEstCommencementDateValue && commencementDateValue !== cdccEstCommencementDateValue) {
      return 'Commencement Date and CDCC Estimated Commencement Date do not match. Please confirm.';
    }
    return null;
  }, [commencementDateValue, cdccEstCommencementDateValue]);

  const isBehindSchedule = getWeeksBehindDirection(weeksBehindField?.value || '') === 'behind';
  const isCommentOnLaborMaterials = getField('CommentOnLaborMaterials')?.value === 'true';

  // Only flag a repeated value while the project is behind — staying on track (0) month-to-month is fine.
  const isWeeksBehindSameAsLastMonth = useMemo(() => {
    const current = (weeksBehindField?.value || '').trim();
    const lastMonth = (weeksBehindField?.lastMonth || '').trim();
    return isBehindSchedule && current === lastMonth;
  }, [isBehindSchedule, weeksBehindField?.value, weeksBehindField?.lastMonth]);

  // Master bookmark for the icon-less field group (see SCHEDULE_MASTER_BOOKMARK_FIELDS).
  // Independent of the section-header "bookmark all" chip — toggling it only affects this group.
  const isMasterBookmarked = SCHEDULE_MASTER_BOOKMARK_FIELDS.every(fieldName => getField(fieldName)?.addToActionItems === true);
  const handleToggleMasterBookmark = () => {
    const newValue = !isMasterBookmarked;
    SCHEDULE_MASTER_BOOKMARK_FIELDS.forEach(fieldName => {
      handleFieldChange(fieldName, { addToActionItems: newValue });
    });
  };

  const customizedLayout = useMemo(() => {
    if (!SCHEDULE_SECTION_CONFIG.layout) return undefined;

    const overrideOptions = (fields?: typeof SCHEDULE_SECTION_CONFIG.layout.leftColumn) =>
      fields?.map(field => {
        if (field.fieldName === budgetFieldName) {
          return { ...field, options: budgetOptions.map(o => o.label) };
        }

        if (field.fieldName === 'CommencementDate') {
          return {
            ...field,
            renderComponent: () => (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <DateFieldRenderer
                    fieldName='CommencementDate'
                    label='Commencement Date'
                    value={getField('CommencementDate')?.value}
                    addToActionItems={getField('CommencementDate')?.addToActionItems}
                    onChange={(value, addToActionItems) => handleFieldChange('CommencementDate', { value: value || '', addToActionItems })}
                    showBookmark={false}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <DateFieldRenderer
                    fieldName='CDCCEstimatedCommencementDate'
                    label='CDCC Estimated Commencement Date'
                    required={!commencementDateValue}
                    value={getField('CDCCEstimatedCommencementDate')?.value}
                    addToActionItems={getField('CDCCEstimatedCommencementDate')?.addToActionItems}
                    onChange={(value, addToActionItems) =>
                      handleFieldChange('CDCCEstimatedCommencementDate', { value: value || '', addToActionItems })
                    }
                    showBookmark={false}
                  />
                </Box>
              </Box>
            ),
          };
        }

        if (field.fieldName === 'OriginalCompletionDate') {
          return {
            ...field,
            type: 'custom-component' as const,
            renderComponent: () => (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <DateFieldRenderer
                    fieldName='OriginalCompletionDate'
                    label='Original Completion Date'
                    value={getField('OriginalCompletionDate')?.value}
                    addToActionItems={getField('OriginalCompletionDate')?.addToActionItems}
                    onChange={(value, addToActionItems) =>
                      handleFieldChange('OriginalCompletionDate', { value: value || '', addToActionItems })
                    }
                    showBookmark={false}
                  />
                </Box>
                {/* Calc Weeks Ahead (Behind), placed to the right of Original Completion Date.
                    Matches DateFieldRenderer's width exactly via the same bookmark-spacer gap. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }} data-field-name='CalcWeeksAheadBehind'>
                  <Box sx={{ flexGrow: 1 }}>
                    <TextField
                      fullWidth
                      label='Calc Weeks Ahead (Behind) based on GC Numbers'
                      value={formatNegativeWithBrackets(calcWeeksField?.value || '')}
                      InputProps={{
                        readOnly: true,
                        sx: (calcWeeksField?.value || '').trim().startsWith('-')
                          ? { color: 'error.main', '& input': { color: 'error.main' } }
                          : undefined,
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                  <Box sx={{ width: 24, flexShrink: 0 }} />
                </Box>
              </Box>
            ),
          };
        }

        if (field.fieldName === 'RevisedCompletionDate') {
          return {
            ...field,
            type: 'custom-component' as const,
            renderComponent: () => (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <DateFieldRenderer
                    fieldName='RevisedCompletionDate'
                    label='Revised Completion Date'
                    value={getField('RevisedCompletionDate')?.value}
                    addToActionItems={getField('RevisedCompletionDate')?.addToActionItems}
                    onChange={(value, addToActionItems) =>
                      handleFieldChange('RevisedCompletionDate', { value: value || '', addToActionItems })
                    }
                    showBookmark={false}
                  />
                </Box>
                {/* Spacer to match the width of a single Commencement Date box */}
                <Box sx={{ flex: 1 }} />
              </Box>
            ),
          };
        }

        if (field.fieldName === 'CDCCEstimatedCompletionDate') {
          return {
            ...field,
            renderComponent: () => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <DateFieldRenderer
                    fieldName='CDCCEstimatedCompletionDate'
                    label='CDCC Estimated Completion Date'
                    value={getField('CDCCEstimatedCompletionDate')?.value}
                    addToActionItems={getField('CDCCEstimatedCompletionDate')?.addToActionItems}
                    onChange={(value, addToActionItems) =>
                      handleFieldChange('CDCCEstimatedCompletionDate', { value: value || '', addToActionItems })
                    }
                    showBookmark={false}
                  />
                </Box>
                {/* Spacer to match the width of a single Commencement Date box, button floats after it */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant='contained'
                    size='small'
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    onClick={() =>
                      handleFieldChange('CDCCEstimatedCompletionDate', {
                        value: revisedCompletionDateValue || getField('OriginalCompletionDate')?.value?.trim() || '',
                      })
                    }
                  >
                    Same as Schedule
                  </Button>
                </Box>
              </Box>
            ),
          };
        }

        // Certificate of Occupancy Issued? + Budget used for calculation stacked on the left, with
        // the inspector-aid checkboxes (moved out of the right column) placed to their right —
        // avoids leaving whitespace under the (shorter) left column while the checkboxes run long.
        // Each checkbox only shows when its answer can affect the Schedule Section Text: the
        // make-it-up / damages questions need Weeks Behind > 0, Adequate? needs Comment on L/M.
        // Hidden answers keep their stored value; getScheduleCommentText ignores them in those states.
        if (field.fieldName === 'CertificateOfOccupancyIssued') {
          const checkboxFields = [
            { fieldName: 'CanTheyMakeItUp', label: 'Can they make it up?', bold: true, visible: isBehindSchedule },
            {
              fieldName: 'WillTheBorrowerAssessLiquidatedDamages',
              label: 'Will the Borrower assess Liquidated Damages?',
              bold: true,
              visible: isBehindSchedule,
            },
            { fieldName: 'CommentOnLaborMaterials', label: 'Comment on Labor/Materials?', bold: false, visible: true },
            { fieldName: 'LaborMaterialsAdequate', label: 'Labor/Materials Adequate?', bold: false, visible: isCommentOnLaborMaterials },
          ].filter(cb => cb.visible);
          return {
            ...field,
            type: 'custom-component' as const,
            renderComponent: () => (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant='body2' sx={{ mb: 0.5, fontWeight: 500 }}>
                      Certificate of Occupancy Issued?
                    </Typography>
                    <SelectFieldRenderer
                      value={getField('CertificateOfOccupancyIssued')?.value}
                      addToActionItems={getField('CertificateOfOccupancyIssued')?.addToActionItems}
                      onChange={(value, addToActionItems) => handleFieldChange('CertificateOfOccupancyIssued', { value, addToActionItems })}
                      fieldName='CertificateOfOccupancyIssued'
                      options={['Yes', 'No']}
                      showBookmark={false}
                    />
                  </Box>
                  <Box>
                    <Typography variant='body2' sx={{ mb: 0.5, fontWeight: 500 }}>
                      Budget used for calculation
                    </Typography>
                    <SelectFieldRenderer
                      value={CALC_MODE_LABELS[calcMode]}
                      addToActionItems={modeField?.addToActionItems}
                      onChange={value => handleModeChange(CALC_MODE_BY_LABEL[value] ?? SCHEDULE_CALC_MODES.single)}
                      fieldName={modeFieldName}
                      options={Object.values(CALC_MODE_LABELS)}
                      showBookmark={false}
                    />
                  </Box>
                  {/* Which G702 drives the calculation — only meaningful in Single mode, and only
                      when the report actually carries its covered draws (legacy reports fall back
                      to the primary-draw mirror instead). */}
                  {calcMode === SCHEDULE_CALC_MODES.single && budgetOptions.length > 0 && (
                    <Box>
                      <Typography variant='body2' sx={{ mb: 0.5, fontWeight: 500 }}>
                        G702 used for calculation
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size='small'
                        value={selectedDraw?.drawId ?? ''}
                        onChange={e => handleFieldChange(budgetFieldName, { value: e.target.value })}
                        inputProps={{ 'aria-label': budgetFieldName }}
                      >
                        {budgetOptions.map(option => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {checkboxFields.map(cb => (
                    <FieldRow
                      key={cb.fieldName}
                      addToActionItems={getField(cb.fieldName)?.addToActionItems}
                      onBookmarkToggle={() => {
                        const f = getField(cb.fieldName);
                        if (f) handleFieldChange(cb.fieldName, { addToActionItems: !f.addToActionItems });
                      }}
                      alignStart
                      showBookmark={false}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={getField(cb.fieldName)?.value === 'true'}
                            onChange={e => handleFieldChange(cb.fieldName, { value: e.target.checked ? 'true' : 'false' })}
                          />
                        }
                        label={cb.label}
                        sx={{
                          m: 0,
                          alignItems: 'center',
                          display: 'flex',
                          '.MuiFormControlLabel-label': {
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            ...(cb.bold && { fontWeight: 600 }),
                          },
                        }}
                      />
                    </FieldRow>
                  ))}
                </Box>
              </Box>
            ),
          };
        }

        if (field.fieldName === weeksBehindFieldName) {
          return {
            ...field,
            renderComponent: () => (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <WeeksBehindFieldRenderer
                    value={weeksBehindField?.value}
                    addToActionItems={weeksBehindField?.addToActionItems}
                    onChange={(value, addToActionItems) => handleFieldChange(weeksBehindFieldName, { value, addToActionItems })}
                    label={field.label}
                    showBookmark={field.showBookmark}
                    isSameAsLastMonth={isWeeksBehindSameAsLastMonth}
                  />
                </Box>
                {/* Matches WeeksBehindFieldRenderer's (and DateFieldRenderer's) width exactly via
                    the same bookmark-spacer gap, so this box's right edge lines up with the rows above/below. */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <TextField
                      fullWidth
                      label='Weeks Behind Noted on Last Report'
                      value={weeksBehindField?.lastMonth || 'N/A'}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                  <Box sx={{ width: 24, flexShrink: 0 }} />
                </Box>
              </Box>
            ),
          };
        }

        if (field.fieldName === 'ScheduledSectionText') {
          return {
            ...field,
            type: 'text-with-refresh' as const,
            onRefresh: () => {
              const autoText = getScheduleCommentText(data);
              lastScheduleAutoTextRef.current = autoText;
              handleFieldChange('ScheduledSectionText', { value: autoText });
            },
          };
        }

        return field;
      });

    return {
      ...SCHEDULE_SECTION_CONFIG.layout,
      // "Bookmark schedule dates" is prepended here (inside the left column) instead of as its
      // own full-width row above the grid, so the left/right columns start flush at the same top.
      // WhichBudgetShouldBeUsedToCalculateTheSchedule is rendered inline under Certificate of
      // Occupancy Issued? above (see that override) instead of as its own row.
      leftColumn: [
        {
          fieldName: '__ScheduleMasterBookmark',
          type: 'custom-component' as const,
          label: '',
          renderComponent: () => (
            // Label then toggle, flush right, so the icon lines up with the per-field bookmark
            // column on the rows below (i.e. above CDCC Estimated Commencement Date).
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <Typography variant='body2' color='text.secondary'>
                Bookmark schedule dates
              </Typography>
              <BookmarkToggle
                addToActionItems={isMasterBookmarked}
                onToggle={handleToggleMasterBookmark}
                ariaLabel='Bookmark schedule dates'
              />
            </Box>
          ),
        },
        ...(overrideOptions(SCHEDULE_SECTION_CONFIG.layout.leftColumn)?.filter(
          f => f.fieldName !== 'WhichBudgetShouldBeUsedToCalculateTheSchedule',
        ) ?? []),
      ],
      rightColumn: [
        {
          fieldName: '__ScheduleFundingGraph',
          type: 'custom-component' as const,
          label: '',
          renderComponent: () =>
            graphData && (reportState.coverPage?.graphType || 'standard') === 'standard' ? (
              // Heading and graph both centered in the right column: the graph is a fixed-width SVG,
              // so left-aligning them left a lopsided gap on the right.
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
                <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 1, mt: 0, lineHeight: 1.2 }}>
                  Funding Schedule Graph
                </Typography>
                <Box
                  role='button'
                  tabIndex={0}
                  onClick={() => setIsGraphDialogOpen(true)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsGraphDialogOpen(true);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 0,
                    border: '1px solid transparent',
                    p: 1,
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.light}`,
                      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.light, 0.13)}`,
                    },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <CoverPageGraphs graphData={graphData} />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      mt: 0.5,
                      opacity: 0.45,
                      width: '100%',
                    }}
                  >
                    <TimelineIcon sx={{ fontSize: 12 }} />
                    <Typography variant='caption' color='text.secondary'>
                      Click to edit the funding schedule graph in a larger view
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : null,
        },
        ...(overrideOptions(SCHEDULE_SECTION_CONFIG.layout.rightColumn)?.filter(
          field =>
            ![
              'CalcWeeksAheadBehind',
              'CanTheyMakeItUp',
              'WillTheBorrowerAssessLiquidatedDamages',
              'CommentOnLaborMaterials',
              'LaborMaterialsAdequate',
            ].includes(field.fieldName),
        ) ?? []),
      ],
      bottomSection: overrideOptions(SCHEDULE_SECTION_CONFIG.layout.bottomSection),
      singleColumn: overrideOptions(SCHEDULE_SECTION_CONFIG.layout.singleColumn),
    };
  }, [
    budgetOptions,
    selectedDraw,
    calcMode,
    modeField?.addToActionItems,
    budgetField?.addToActionItems,
    commencementDateValue,
    revisedCompletionDateValue,
    isWeeksBehindSameAsLastMonth,
    isBehindSchedule,
    isCommentOnLaborMaterials,
    weeksBehindField,
    data,
    getField,
    handleFieldChange,
    graphData,
    reportState.coverPage?.graphType,
    theme,
    isMasterBookmarked,
    handleToggleMasterBookmark,
  ]);

  // Normalize the stored budget selection to a drawId: reports written before budgets were
  // selectable hold a draw name (or nothing at all), and resolveScheduleBudgetDraw has already
  // mapped that to the draw the calculation is actually using.
  useEffect(() => {
    if (!budgetField || !selectedDraw) return;
    if (budgetField.value !== selectedDraw.drawId) {
      handleFieldChange(budgetFieldName, { value: selectedDraw.drawId });
    }
  }, [selectedDraw, budgetField?.value, handleFieldChange]);

  useEffect(() => {
    if (!calcWeeksField) return;

    if (calcWeeksField.value !== calcWeeksAheadBehind) {
      handleFieldChange(calcWeeksFieldName, { value: calcWeeksAheadBehind });
    }
  }, [calcWeeksAheadBehind, calcWeeksField?.value, handleFieldChange]);

  // Seed WeeksBehindToReport with 0 when the field is empty on first mount.
  // If the DB already has a value (user-set or carried over from reset), leave it alone.
  useEffect(() => {
    if (weeksBehindSeededRef.current) return;
    if (!weeksBehindField) return;
    weeksBehindSeededRef.current = true;
    if (!weeksBehindField.value?.trim()) {
      handleFieldChange(weeksBehindFieldName, { value: '0' });
    }
  }, [weeksBehindField, handleFieldChange]);

  // Stable key built from only the fields getScheduleCommentText reads, so the effect below
  // does not re-run when ScheduledSectionText itself is updated by handleFieldChange.
  const scheduleInputKey = useMemo(() => {
    const SCHEDULE_INPUT_FIELDS = [
      'WeeksBehindToReport',
      'CanTheyMakeItUp',
      'WillTheBorrowerAssessLiquidatedDamages',
      'CommentOnLaborMaterials',
      'LaborMaterialsAdequate',
      'LiquidatedDamagesPerContract',
      'CommencementDate',
      'OriginalCompletionDate',
      'RevisedCompletionDate',
      'CDCCEstimatedCompletionDate',
    ];
    return SCHEDULE_INPUT_FIELDS.map(name => data?.fields.find(f => f.fieldName === name)?.value ?? '').join('|');
  }, [data]);

  // Auto-populate Schedule Section Text from schedule comment conditions (comment stays only in Schedule Section Text, not in Comments).
  // Users can edit the text; it will only be refreshed when the underlying schedule conditions change.
  useEffect(() => {
    const autoText = getScheduleCommentText(data);
    const scheduledSectionTextField = getField('ScheduledSectionText');
    if (!scheduledSectionTextField) return;

    // On first run, seed the ref to autoText so subsequent re-runs exit via the
    // `autoText === ref` guard without touching the field. If the field is empty
    // we also write autoText so the field gets its initial value.
    if (lastScheduleAutoTextRef.current === null) {
      lastScheduleAutoTextRef.current = autoText;
      if (!scheduledSectionTextField.value) {
        handleFieldChange('ScheduledSectionText', { value: autoText });
      }
      return;
    }

    if (autoText === lastScheduleAutoTextRef.current) return;

    // Only overwrite if field is empty or still shows the last auto-generated text
    // (i.e. user has not manually edited it since the last auto-gen).
    const isEmpty = !scheduledSectionTextField.value;
    const matchesPreviousAuto = scheduledSectionTextField.value === lastScheduleAutoTextRef.current;
    if (isEmpty || matchesPreviousAuto) {
      lastScheduleAutoTextRef.current = autoText;
      handleFieldChange('ScheduledSectionText', { value: autoText });
    }
  }, [scheduleInputKey, getField, handleFieldChange]);

  // Stable key for the construction progress schedule effect — all 5 schedule date fields.
  const progressScheduleInputKey = useMemo(() => {
    const getDate = (name: string) => data?.fields.find(f => f.fieldName === name)?.value ?? '';
    return ['CommencementDate', 'OriginalCompletionDate', 'RevisedCompletionDate', 'BankLoanExpirationDate', 'CDCCEstimatedCompletionDate']
      .map(getDate)
      .join('|');
  }, [data]);

  // "Dates are Estimated" vs "Schedule Provided" is now derived (not user-toggled): Commencement Date
  // present -> Schedule Provided comments; Commencement Date blank -> Dates are Estimated comments.
  const datesAreEstimated = !commencementDateValue;

  // Keep the underlying DatesAreEstimated field in sync so other consumers (e.g. report export
  // fallback text) that read it directly still see the correct derived value.
  useEffect(() => {
    const datesAreEstimatedField = getField('DatesAreEstimated');
    if (!datesAreEstimatedField) return;
    const desired = String(datesAreEstimated);
    if (datesAreEstimatedField.value !== desired) {
      handleFieldChange('DatesAreEstimated', { value: desired });
    }
  }, [datesAreEstimated, getField, handleFieldChange]);

  // All 5 schedule date fields are empty → treat as "no schedule provided" regardless of toggle.
  const allDatesEmpty = useMemo(() => {
    return [
      'CommencementDate',
      'OriginalCompletionDate',
      'RevisedCompletionDate',
      'BankLoanExpirationDate',
      'CDCCEstimatedCompletionDate',
    ].every(name => !(data?.fields.find(f => f.fieldName === name)?.value || '').trim());
  }, [progressScheduleInputKey]);

  const constructionProgressScheduleFieldValue = getField('ConstructionProgressScheduleText')?.value;

  // Sync ConstructionProgressScheduleText: NO_DATES when toggle is "estimated" or all dates empty; HAS_DATES otherwise.
  useEffect(() => {
    // Wait until localData has been populated (data is undefined until the section loads)
    if (!data) return;

    const autoText =
      datesAreEstimated || allDatesEmpty ? CONSTRUCTION_PROGRESS_SCHEDULE_NO_DATES_TEXT : CONSTRUCTION_PROGRESS_SCHEDULE_HAS_DATES_TEXT;
    const field = getField('ConstructionProgressScheduleText');
    const currentValue = field?.value ?? '';

    // Seed the ref to autoText on first run so subsequent re-runs exit via the
    // `autoText === ref` guard. If the field is empty we also write autoText.
    if (lastProgressScheduleAutoTextRef.current === null) {
      lastProgressScheduleAutoTextRef.current = autoText;
      if (!currentValue) {
        handleFieldChange('ConstructionProgressScheduleText', { value: autoText });
      }
      return;
    }
    if (autoText === lastProgressScheduleAutoTextRef.current) return;
    const isEmpty = !currentValue;
    const matchesPreviousAuto = currentValue === lastProgressScheduleAutoTextRef.current;
    if (isEmpty || matchesPreviousAuto) {
      handleFieldChange('ConstructionProgressScheduleText', { value: autoText });
      lastProgressScheduleAutoTextRef.current = autoText;
    }
  }, [datesAreEstimated, allDatesEmpty, constructionProgressScheduleFieldValue, data, getField, handleFieldChange]);

  if (!SCHEDULE_SECTION_CONFIG.layout) {
    return <div>Layout configuration missing</div>;
  }

  // Handle field deletion (clear content)
  const handleDeleteField = (fieldName: string) => {
    handleFieldChange(fieldName, { value: '', addToActionItems: false });
  };

  const handleRefreshConstructionProgressScheduleText = () => {
    const autoText =
      datesAreEstimated || allDatesEmpty ? CONSTRUCTION_PROGRESS_SCHEDULE_NO_DATES_TEXT : CONSTRUCTION_PROGRESS_SCHEDULE_HAS_DATES_TEXT;
    lastProgressScheduleAutoTextRef.current = autoText;
    handleFieldChange('ConstructionProgressScheduleText', { value: autoText });
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        {commencementDateError && (
          <Alert severity='error' sx={{ mb: 1.5 }}>
            {commencementDateError}
          </Alert>
        )}
        <DeclarativeLayoutRenderer
          layout={{
            singleColumn: [
              {
                fieldName: 'ConstructionProgressScheduleText',
                type: 'text-with-refresh',
                label: 'Construction Progress Schedule',
                onRefresh: handleRefreshConstructionProgressScheduleText,
              },
            ],
          }}
          getField={getField}
          handleFieldChange={handleFieldChange}
          onDeleteField={handleDeleteField}
          onBook={onBook}
        />
      </Box>

      {/* Config-driven layout rendering - just pass the config! ("Bookmark all schedule fields" is
          now the first item inside the left column, see customizedLayout above.) */}
      <Box ref={layoutContainerRef}>
        <DeclarativeLayoutRenderer
          layout={customizedLayout || SCHEDULE_SECTION_CONFIG.layout}
          getField={getField}
          handleFieldChange={handleFieldChange}
          onDeleteField={handleDeleteField}
          onBook={onBook}
        />
      </Box>

      {/* Comments Section */}
      {commentsField && (
        <CommentsSection
          commentsField={commentsField}
          onChange={updated => handleFieldChange('Comments', updated)}
          title='Comments'
          onBook={onBook}
          showBookmark={true}
        />
      )}

      {/* All-G702s warning. Cancel leaves the stored mode untouched, so the select falls back to
          whatever was selected before. */}
      <Dialog open={isAllWarningOpen} onClose={() => setIsAllWarningOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Use all G702s?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>{ALL_G702_WARNING_TEXT}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAllWarningOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            sx={{ color: theme.palette.common.white }}
            onClick={() => {
              handleFieldChange(modeFieldName, { value: SCHEDULE_CALC_MODES.all });
              setIsAllWarningOpen(false);
            }}
          >
            Proceed with All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Funding Schedule Graph dialog — moved here from Amounts for Cover Page */}
      <Dialog
        open={isGraphDialogOpen}
        onClose={() => setIsGraphDialogOpen(false)}
        fullWidth
        maxWidth='xl'
        PaperProps={{ sx: { maxHeight: '650px', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ pb: 1, flexShrink: 0 }}>Funding Schedule Graph</DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'stretch', flex: 1, overflow: 'hidden' }}>
            {/* Left: graph + axis controls */}
            <Box
              sx={{
                // Graph width + p: 2 on both sides (2 x 16px) + 1px right border, so the fixed-width graph never gets clipped.
                width: SCHEDULE_GRAPH_LAYOUT.width + 33,
                flexShrink: 0,
                borderRight: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                gap: 1.5,
                backgroundColor: theme.palette.grey[50],
                overflowY: 'auto',
              }}
            >
              <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                Schedule Graph
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {graphData && <CoverPageGraphs graphData={graphData} hoveredFundingId={hoveredFundingId} />}
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={scheduleGraphAxis?.hideCommencement ?? false}
                    onChange={e => handleHideCommencementChange(e.target.checked)}
                  />
                }
                label='Hide commencement point'
                sx={{ m: 0, display: 'flex', alignItems: 'center' }}
              />

              {/* Axis warnings — axis dates are now derived from the Schedule module's own date fields. */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {graphData?.scheduleData?.startDateExceeded && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.error.main}`,
                      backgroundColor: alpha(theme.palette.error.main, 0.06),
                    }}
                  >
                    <Typography variant='caption' sx={{ color: theme.palette.error.main, fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Data precedes start date
                    </Typography>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                      Please update the commencement date in the Schedule module.
                    </Typography>
                  </Box>
                )}
                {graphData?.scheduleData?.endDateExceeded && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.error.main}`,
                      backgroundColor: alpha(theme.palette.error.main, 0.06),
                    }}
                  >
                    <Typography variant='caption' sx={{ color: theme.palette.error.main, fontWeight: 600, display: 'block', mb: 0.5 }}>
                      Data extends past end date
                    </Typography>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                      Please update the completion date in the Schedule module.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Right: schedule graph table */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <ScheduleGraphTable
                rows={scheduleGraph ?? []}
                revisedBudget={reportState.budgetSummary?.contractBudgetSumToDate}
                onChange={handleScheduleGraphChange}
                onHoveredRowId={setHoveredFundingId}
                commencementDate={scheduleGraphAxis?.hideCommencement ? undefined : graphData?.scheduleData?.axisStartDate}
                previousFundings={reportState.drawSummary?.previousFundings}
                currentRetainagePercent={reportState.budgetSummary?.totalRetainageHeldPercentage}
                currentContractSumToDate={reportState.budgetSummary?.contractBudgetSumToDate}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsGraphDialogOpen(false)} variant='contained' sx={{ color: theme.palette.common.white }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const ScheduleSection: React.FC<ScheduleSectionProps> = ({ magicReportId }) => {
  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={SCHEDULE_SECTION_CONFIG}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <ScheduleSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
